type RetryOptions = {
  attempts?: number;
  delayMs?: number;
  signal?: AbortSignal;
  shouldRetry?: (error: unknown) => boolean;
};

function getErrorStatus(error: unknown) {
  if (!error || typeof error !== "object") return null;

  if ("status" in error && typeof error.status === "number") {
    return error.status;
  }

  if (
    "response" in error &&
    error.response &&
    typeof error.response === "object" &&
    "status" in error.response &&
    typeof error.response.status === "number"
  ) {
    return error.response.status;
  }

  return null;
}

function isAbortError(error: unknown) {
  if (!error || typeof error !== "object") return false;

  return (
    (error instanceof DOMException && error.name === "AbortError") ||
    ("name" in error &&
      (error.name === "AbortError" || error.name === "CanceledError"))
  );
}

export function isRetriableRequestError(error: unknown) {
  if (isAbortError(error)) return false;

  const status = getErrorStatus(error);
  if (status === null) return true;

  return status >= 500 || status === 408 || status === 429;
}

async function wait(delayMs: number, signal?: AbortSignal) {
  if (delayMs <= 0) return;
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

  await new Promise<void>((resolve, reject) => {
    let timeout: NodeJS.Timeout;

    // Define the abort handler so we can reference it for removal
    const onAbort = () => {
      clearTimeout(timeout);
      reject(new DOMException("Aborted", "AbortError"));
    };

    // If the timer succeeds, clean up the event listener
    timeout = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, delayMs);

    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

export async function withRetry<T>(
  operation: () => Promise<T>,
  {
    attempts = 3,
    delayMs = 300,
    signal,
    shouldRetry = isRetriableRequestError,
  }: RetryOptions = {},
) {
  let lastError: unknown;

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

    try {
      return await operation();
    } catch (error) {
      lastError = error;

      if (attempt >= attempts || !shouldRetry(error) || signal?.aborted) {
        throw error;
      }

      await wait(delayMs * attempt, signal);
    }
  }

  throw lastError;
}
